import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import Layout from './Layout';
import SmartImage from './SmartImage';
import AddToCartControl from './common/AddToCartControl';
// Removed toast notifications - using inline button feedback instead
import './Products.css';

interface Product {
  id: number;
  sos_id?: number;
  sku: string;
  name: string;
  fullname?: string;
  description?: string;
  sales_price?: number;
  price?: number;
  available: number;
  category_name?: string;
  category?: string;
  vendor_name?: string;
  vendor?: string;
  is_active?: boolean;
  taxable?: boolean;
  weight?: number;
  weight_unit?: string;
  volume?: number;
  volume_unit?: string;
  custom_fields?: any;
  image_path?: string;
  image_url?: string;
  image_updated_at?: string;
  minimum_order_quantity: number;
  model?: string;
  related_products?: string[];
  // Compatibility flags from gateway
  s1_compatible?: boolean;
  m1_compatible?: boolean;
  m2_compatible?: boolean;
  lg6500_compatible?: boolean;
  lg6500_marriott_compatible?: boolean;
  // Additional gateway fields
  feature_highlights?: string;
  is_purchasable?: boolean;
  important_note?: string;
  manufacturer?: string;
  onhand?: number;
  has_image?: boolean;
}



interface UserProperty {
  siteid: number;
  sitename: string;
  product?: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  sites?: UserProperty[];
}

const Products: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [user, setUser] = useState<User | null>(null);
  const [siteProduct, setSiteProduct] = useState<string>('');
  const [cartItems, setCartItems] = useState<Map<number, number>>(new Map());
  const [localQuantities, setLocalQuantities] = useState<Map<number, number>>(new Map());

  // Load user and determine site product
  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await api.get('/users/me');
        setUser(response.data);

        // Get site's product
        if (response.data.sites && response.data.sites.length > 0) {
          const site = response.data.sites[0];
          setSiteProduct(site.product || '');
          console.log('Site product:', site.product);
        }
      } catch (error) {
        console.error('Failed to load user:', error);
      }
    };

    loadUser();
  }, []);

  // Track window width for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      console.log('📐 Window resized:', {
        newWidth,
        hasSidebar: newWidth >= 768,
        availableSpace: newWidth - (newWidth >= 768 ? 260 : 0)
      });
    };
    window.addEventListener('resize', handleResize);

    // Log initial width
    console.log('📐 Initial window width:', {
      width: windowWidth,
      hasSidebar: windowWidth >= 768,
      availableSpace: windowWidth - (windowWidth >= 768 ? 260 : 0)
    });

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load cart items on mount and when cart updates
  useEffect(() => {
    loadCartItems();

    const handleCartUpdate = () => {
      loadCartItems();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const loadCartItems = async () => {
    try {
      const response = await api.get('/cart');
      const cartMap = new Map<number, number>();

      // Handle both response structures
      const items = response.data?.items || response.data?.cart?.items || [];

      if (Array.isArray(items)) {
        items.forEach((item: any) => {
          // Get product_id from either the item directly or from item.product
          const productId = item.product_id || item.product?.id;
          if (productId) {
            cartMap.set(productId, item.quantity);
          }
        });
      }

      console.log('Cart items loaded:', Array.from(cartMap.entries()));
      setCartItems(cartMap);
      // Also initialize localQuantities so the Total line displays on page load
      setLocalQuantities(new Map(cartMap));
    } catch (error) {
      console.error('Failed to load cart items:', error);
    }
  };

  // Map site product to compatibility filter
  const getCompatibilityFilter = (siteProductName: string): { [key: string]: boolean } | null => {
    if (!siteProductName) return null;

    // Check for uppercase GRE before converting to lowercase
    if (siteProductName.includes('GRE')) {
      return { gre_compatible: true };
    }

    const product = siteProductName.toLowerCase();

    // OnStream M1 → M1 compatible
    if (product.includes('onstream m1')) {
      return { m1_compatible: true };
    }

    // OnStream M2 → M2 compatible
    if (product.includes('onstream m2')) {
      return { m2_compatible: true };
    }

    // PureDISH, PureSTREAM, PureGUIDE → S1 compatible
    if (product.includes('puredish') || product.includes('purestream') || product.includes('pureguide')) {
      return { s1_compatible: true };
    }

    // Default: show all products if we don't recognize the product
    return null;
  };

  // Helper function needs to be defined before being used in useCallback
  const getProductDisplayName = (product: Product): string => {
    // Use name field (which contains ShortDescription) as the primary display name
    // Fall back to fullname, then description if name is not available
    return product.name || product.fullname || product.description || '';
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);

      // Get compatibility filter based on site product
      const compatibilityFilter = getCompatibilityFilter(siteProduct);

      console.log('Loading products with filter:', {
        siteProduct,
        filter: compatibilityFilter
      });

      // Use products endpoint (works with both products) - UPDATED
      const response = await api.get<any>('/products/', {
        params: {
          page: 1,
          page_size: 100,
          sort_by: 'sell',
          sort_order: 'desc',
          ...compatibilityFilter
        }
      });

      // API returns paginated response: { items, total, page, page_size, pages }
      const { items, total } = response.data;

      setProducts(items || []);
      console.log('Products loaded:', items?.length || 0, 'out of', total || 0, 'total');
    } catch (error) {
      console.error('Failed to load products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [siteProduct]);

  // Load products when component mounts and when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadProducts();
    }, 100); // Small debounce to avoid rapid re-renders
    
    return () => clearTimeout(timeoutId);
  }, [loadProducts]);
  

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handleProductClick = (product: Product) => {
    navigate(`/products/${product.id}`);
  };



  const renderProductCard = (product: Product, index: number) => {
    // Calculate number of columns based on available width (accounting for sidebar)
    const sidebarWidth = 260;
    const hasSidebar = windowWidth >= 768;
    const availableWidth = windowWidth - (hasSidebar ? sidebarWidth : 0);

    let columns;
    if (availableWidth < 576) {
      columns = 2; // Mobile
    } else if (availableWidth < 768) {
      columns = 3; // Small
    } else if (availableWidth < 992) {
      columns = 4; // Medium
    } else if (availableWidth < 1400) {
      columns = 5; // Large
    } else {
      columns = 6; // Extra large
    }

    // Log debug info for first product only to avoid spam
    if (index === 0) {
      console.log('🔍 Products Grid Debug:', {
        windowWidth,
        hasSidebar,
        sidebarWidth: hasSidebar ? sidebarWidth : 0,
        availableWidth,
        calculatedColumns: columns,
        breakpoint: availableWidth < 576 ? 'xs' :
                   availableWidth < 768 ? 'sm' :
                   availableWidth < 992 ? 'md' :
                   availableWidth < 1400 ? 'lg' : 'xl'
      });
    }

    const columnWidth = `calc(${100 / columns}% - 16px)`;
    const localQuantity = localQuantities.get(product.id) || 0;
    const cartQuantity = cartItems.get(product.id) || 0;
    const unitPrice = product.sales_price || product.price || 0;
    const totalPrice = unitPrice * localQuantity;

    return (
      <div
        key={product.id}
        style={{
          flex: `0 0 ${columnWidth}`,
          maxWidth: columnWidth,
          marginBottom: '12px',
          padding: '0 6px'
        }}
      >
        <div className="card h-100 product-card">
          {/* Product image */}
          <div
            className="product-image-wrapper"
            onClick={() => handleProductClick(product)}
            style={{ cursor: 'pointer' }}
          >
            <SmartImage
              src={product.image_url || (product.id ? `/images/products/${product.id}` : undefined)}
              alt={product.name}
              className="product-image"
              productName={product.name}
              productCategory={product.category || product.category_name}
              productSku={product.sku}
            />
          </div>

          <div className="card-body d-flex flex-column" style={{ padding: '12px' }}>
            <h6
              className="card-title"
              onClick={() => handleProductClick(product)}
              style={{
                fontSize: '14px',
                marginBottom: '8px',
                minHeight: '2.4em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: '1.2',
                cursor: 'pointer'
              }}
              title={getProductDisplayName(product)}
            >
              {getProductDisplayName(product)}
            </h6>

            <div
              className="text-muted"
              onClick={() => handleProductClick(product)}
              style={{ fontSize: '12px', marginBottom: '8px', cursor: 'pointer' }}
            >
              {product.sku}
            </div>

            <div className="mt-auto">
              {/* Price display */}
              <div className="mb-1" style={{ fontSize: '13px' }}>
                <div
                  className="d-flex justify-content-between align-items-center"
                  onClick={() => handleProductClick(product)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="text-muted" style={{ fontSize: '12px' }}>Unit:</span>
                  <strong className="text-primary" style={{ fontSize: '14px' }}>{formatCurrency(unitPrice)}</strong>
                </div>
                {localQuantity > 0 && (
                  <div
                    className="d-flex justify-content-between align-items-center"
                    onClick={() => handleProductClick(product)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="text-muted" style={{ fontSize: '12px' }}>Total:</span>
                    <strong className="text-success" style={{ fontSize: '14px' }}>{formatCurrency(totalPrice)}</strong>
                  </div>
                )}
                {product.minimum_order_quantity > 1 && (
                  <small className="text-info d-block" style={{ fontSize: '11px' }}>Min: {product.minimum_order_quantity}</small>
                )}
              </div>

              {/* Quantity editor and Add button */}
              <div>
                {product.is_purchasable === false ? (
                  <div className="text-center text-muted py-2">
                    <small>Not available for direct purchase</small>
                  </div>
                ) : (
                  <AddToCartControl
                    key={`product-${product.id}-cart-${cartQuantity}`}
                    productId={product.id}
                    minimumOrderQuantity={product.minimum_order_quantity}
                    size="sm"
                    initialQuantity={cartQuantity}
                    onQuantityChange={(qty) => setLocalQuantities(prev => new Map(prev).set(product.id, qty))}
                    onCartUpdate={() => loadCartItems()}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  return (
    <Layout>
      <div className="products-page-container" style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: '10px 1.5rem'
      }}>
        {/* Title */}
        <div style={{
          marginBottom: '20px',
          marginLeft: '-8px',
          marginRight: '-8px',
          paddingLeft: '6px',
          paddingRight: '6px'
        }}>
          <h2 className="mb-0" style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#2c3e50'
          }}>
            <i className="fas fa-box me-2"></i>
            {siteProduct ? `${siteProduct} ` : ''}Product Catalog
          </h2>
        </div>

        {/* Products Display */}
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {products.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="fas fa-box fa-3x text-muted mb-3"></i>
                  <h4>No products available</h4>
                  <p className="text-muted">Please check back later.</p>
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                marginLeft: '-8px',
                marginRight: '-8px'
              }}>
                {products.map((product, index) => renderProductCard(product, index))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Custom styles */}
      <style>{`
        .products-page-container {
          width: 100%;
        }
        
        .product-card {
          transition: transform 0.2s, box-shadow 0.2s;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .product-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }
        
        .product-list-item {
          transition: box-shadow 0.2s;
        }
        
        .product-list-item:hover {
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        
        .product-image-wrapper {
          height: 160px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
          overflow: hidden;
        }
        
        .product-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 8px;
        }
        
        .product-image-placeholder {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #6c757d;
        }
        
        .product-image-placeholder-list {
          width: 80px;
          height: 80px;
          background-color: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
        }
        
        .product-list-image {
          width: 80px;
          height: 80px;
          object-fit: contain;
          border-radius: 8px;
        }
        
        .price {
          font-weight: 600;
        }
        
        .btn-group .btn {
          padding: 0.375rem 0.75rem;
        }
      `}</style>

      {/* Removed Toast Notifications - Using inline button feedback instead */}
    </Layout>
  );
};

export default Products;